import { useLocation } from 'react-router-dom';
import { isFranchisePassportCheckinEnabled } from '../../shared/franchiseFlowAuth';

export const FACE_IDENTITY_BASE = '/enterprise/face-identity';
export const PASSPORT_BASE = '/enterprise/passport';
export const GUEST_CHECKIN_BASE = '/enterprise/guest-checkin';

/** Phone Guest check-in QR flow (no Passport kiosk scan gate). */
export function isGuestCheckInFlow(pathname = '') {
  return String(pathname).startsWith(GUEST_CHECKIN_BASE);
}

/** Legacy phone QR under /passport/guest (also no kiosk scan gate). */
export function isPassportGuestPath(pathname = '') {
  const path = String(pathname || '');
  return path.startsWith(`${PASSPORT_BASE}/guest`) || path.startsWith(`${PASSPORT_BASE}/entry`);
}

/**
 * Passport tablet/kiosk flow with partner start/stop video.
 * Excludes guest-checkin and /passport/guest phone paths.
 */
export function isPassportFlow(pathname = '') {
  const path = String(pathname || '');
  if (isGuestCheckInFlow(path) || isPassportGuestPath(path)) return false;
  return path.startsWith(PASSPORT_BASE);
}

/**
 * Queues guests on partner check-ins for register/edit (Passport kiosk + Guest QR).
 */
export function isPassportCheckinBoardFlow(pathname = '') {
  return isPassportFlow(pathname) || isGuestCheckInFlow(pathname) || isPassportGuestPath(pathname);
}

export function resolveFaceIdentityBase(pathname = '') {
  const path = String(pathname || '');
  if (isGuestCheckInFlow(path)) {
    return GUEST_CHECKIN_BASE;
  }
  if (isPassportGuestPath(path)) {
    return `${PASSPORT_BASE}/guest`;
  }
  if (path.startsWith(`${FACE_IDENTITY_BASE}/guest`)) {
    return `${FACE_IDENTITY_BASE}/guest`;
  }
  if (path.startsWith(PASSPORT_BASE)) {
    return PASSPORT_BASE;
  }
  return FACE_IDENTITY_BASE;
}

export function resolveFaceApiPrefix(pathname = '') {
  // Guest QR uses guest JWT against the standard face API.
  if (isGuestCheckInFlow(pathname) || isPassportGuestPath(pathname)) {
    return '/api/salon/face';
  }
  return isPassportFlow(pathname) ? '/api/salon/passport/face' : '/api/salon/face';
}

export function faceIdentityRoute(segment, pathname) {
  const base = resolveFaceIdentityBase(pathname);
  const tail = segment ? String(segment).replace(/^\//, '') : '';
  return tail ? `${base}/${tail}` : base;
}

/** Post-login / home target — Passport + guest-checkin open camera directly. */
export function faceIdentityHomePath(pathname = '') {
  if (isGuestCheckInFlow(pathname)) {
    return `${GUEST_CHECKIN_BASE}/scan`;
  }
  if (isPassportFlow(pathname)) {
    return isFranchisePassportCheckinEnabled()
      ? `${PASSPORT_BASE}/scan`
      : `${PASSPORT_BASE}/mobile`;
  }
  const base = resolveFaceIdentityBase(pathname);
  if (base.endsWith('/guest')) {
    return `${base}/scan`;
  }
  return base;
}

export function useFaceIdentityBase() {
  const { pathname } = useLocation();
  return resolveFaceIdentityBase(pathname);
}

export function useFaceApiPrefix() {
  const { pathname } = useLocation();
  return resolveFaceApiPrefix(pathname);
}
